import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import { useTranslation } from 'react-i18next';
import { useActionSettings } from '../../hooks/useActionSettings';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';

const ActionsReplaceWordText = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();

  const [sourcePath, setSourcePath] = useState('');
  const [targetPath, setTargetPath] = useState('/{filename}_replaced.docx');
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [replaceIfExists, setReplaceIfExists] = useState(false);
  const [sourceBrowseDisabled, setSourceBrowseDisabled] = useState(false);
  const [targetBrowseDisabled, setTargetBrowseDisabled] = useState(false);

  const sourceRef = useRef(null);
  const targetRef = useRef(null);
  const findTextRef = useRef(null);
  const replaceTextRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const availableTags = useMemo(() => {
    if (!tags || !Array.isArray(tags)) {
      return [];
    }

    return tags.map((tag) => ({
      key: tag?.key || tag?.value || tag?.variable || tag?.label,
      label: tag?.label || tag?.key || tag?.value || tag?.variable,
      value: tag?.value || tag?.variable || tag?.key || tag?.label,
      variable: tag?.variable || tag?.value || tag?.key || tag?.label,
      description: tag?.description || '',
      type: tag?.type || 'text',
      reply: tag?.reply || '',
      tag_type: tag?.tag_type || '',
    })).filter((tag) => tag.key && tag.value);
  }, [tags]);

  const {
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleConditionsChange,
    handleSave: baseHandleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'replacewordtext',
    onSaveStatusChange,
    customFields: {
      sourceFilePath: '',
      targetPath: '/{filename}_replaced.docx',
      findText: '',
      replaceText: '',
      replaceIfExists: false,
    },
    customComparison: (initial) => (
      (initial.sourceFilePath || '') === sourcePath &&
      (initial.targetPath || '') === targetPath &&
      (initial.findText || '') === findText &&
      (initial.replaceText || '') === replaceText &&
      Boolean(initial.replaceIfExists) === replaceIfExists
    ),
    customSaveData: () => ({
      sourceFilePath: sourcePath,
      targetPath,
      findText,
      replaceText,
      replaceIfExists,
    }),
    loadDataFunction: (data) => {
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (error) {
          console.error('Failed to parse replace word text action data:', error);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }

      if (parsedData.sourceFilePath !== undefined) {
        setSourcePath(parsedData.sourceFilePath || '');
      }
      if (parsedData.targetPath !== undefined) {
        setTargetPath(parsedData.targetPath || '/{filename}_replaced.docx');
      }
      if (parsedData.findText !== undefined) {
        setFindText(parsedData.findText || '');
      }
      if (parsedData.replaceText !== undefined) {
        setReplaceText(parsedData.replaceText || '');
      }
      if (parsedData.replaceIfExists !== undefined) {
        setReplaceIfExists(Boolean(parsedData.replaceIfExists));
      }
    },
    enableConditions: true,
  });

  const { handleBrowseDirectory: handleBrowseSourceFile, FolderBrowserComponent: SourceFileBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (filePath) => {
      if (filePath) {
        setSourcePath(filePath);
      }
      setSourceBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setSourceBrowseDisabled(false);
    },
  });

  const { handleBrowseDirectory: handleBrowseTargetDirectory, FolderBrowserComponent: TargetFolderBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    onFolderSelected: (folderPath) => {
      if (!folderPath) {
        return;
      }
      const cleanPath = folderPath.replace(/[\\/]+$/, '');
      const separator = cleanPath.includes('\\') ? '\\' : '/';
      setTargetPath(`${cleanPath}${separator}{filename}_replaced.docx`);
      setTargetBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setTargetBrowseDisabled(false);
    },
  });

  const handleSave = useCallback(() => {
    baseHandleSave({
      sourceFilePath: sourcePath,
      targetPath,
      findText,
      replaceText,
      replaceIfExists,
      useConditions: true,
      conditions,
    });
  }, [
    baseHandleSave,
    conditions,
    findText,
    replaceIfExists,
    replaceText,
    sourcePath,
    targetPath,
  ]);

  const renderEditorCard = (label, badge, editorRef, value, onChange, placeholder, extraAction = null) => (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #5757b010',
        overflow: 'hidden',
      }}
    >
      <CardHeader
        title={(
          <Typography
            sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              component="span"
              sx={{
                bgcolor: '#5757b0',
                color: 'white',
                borderRadius: '6px',
                px: 1.5,
                py: 0.7,
                mr: { xs: 1, sm: 2 },
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
                fontWeight: 600,
              }}
            >
              {badge}
            </Box>
            {label}
          </Typography>
        )}
        sx={{
          borderBottom: '1px solid #f0f0f8',
          backgroundColor: '#fafbff',
          p: { xs: 1.5, sm: 2.5 },
        }}
      />
      <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
        <Box
          sx={{
            mb: 1.5,
            border: '1px solid #d0d0e8',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#fff',
            '&:focus-within': {
              borderColor: '#5757b0',
              boxShadow: '0 0 0 3px rgba(87, 87, 176, 0.1)',
            },
          }}
        >
          <Box sx={{ padding: '12px 16px', minHeight: '28px' }}>
            <TagEditor
              caseid={caseid}
              ref={editorRef}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              singleLine
              tags={availableTags}
            />
          </Box>
        </Box>
        {extraAction}
      </CardContent>
    </Card>
  );

  return (
    <ActionSettingsLayout
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      title={t('workflow.replace_word_text', 'Replace Word Text')}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={availableTags}
      enableConditions={true}
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      primaryColor="#5757b0"
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          {renderEditorCard(
            t('workflow.source_file_path', 'Source DOCX File'),
            'SOURCE',
            sourceRef,
            sourcePath,
            setSourcePath,
            t('workflow.replace_word_text.source_placeholder', 'C:\\output\\source.docx'),
            <Button
              variant="contained"
              startIcon={<FolderOpenIcon />}
              onClick={() => {
                setSourceBrowseDisabled(true);
                handleBrowseSourceFile();
              }}
              disabled={sourceBrowseDisabled}
              sx={{ backgroundColor: '#5757b0' }}
            >
              {t('workflow.browse', 'Browse')}
            </Button>,
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderEditorCard(
            t('workflow.target_path', 'Target Path'),
            'TARGET',
            targetRef,
            targetPath,
            setTargetPath,
            t('workflow.replace_word_text.target_placeholder', '/{filename}_replaced.docx'),
            <Button
              variant="contained"
              startIcon={<FolderOpenIcon />}
              onClick={() => {
                setTargetBrowseDisabled(true);
                handleBrowseTargetDirectory();
              }}
              disabled={targetBrowseDisabled}
              sx={{ backgroundColor: '#5757b0' }}
            >
              {t('workflow.browse', 'Browse')}
            </Button>,
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderEditorCard(
            t('workflow.replace_word_text.find_label', 'Find Text'),
            'FIND',
            findTextRef,
            findText,
            setFindText,
            t('workflow.replace_word_text.find_placeholder', 'Text to find in the DOCX document'),
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderEditorCard(
            t('workflow.replace_word_text.replace_label', 'Replace With'),
            'REPLACE',
            replaceTextRef,
            replaceText,
            setReplaceText,
            t('workflow.replace_word_text.replace_placeholder', 'Replacement text'),
          )}
        </Grid>
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
              border: '1px solid #5757b010',
            }}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FindReplaceIcon sx={{ color: '#5757b0' }} />
              <Box sx={{ flex: 1 }}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={replaceIfExists}
                      onChange={(event) => setReplaceIfExists(event.target.checked)}
                    />
                  )}
                  label={t(
                    'workflow.replace_word_text.replace_if_exists',
                    'Replace target file if it already exists',
                  )}
                />
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'workflow.replace_word_text.replace_if_exists_help',
                    'When enabled, the generated DOCX will overwrite an existing file at the target path.',
                  )}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {SourceFileBrowser}
      {TargetFolderBrowser}
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsReplaceWordText);
